import { CollectibleId_Schema } from "@/services/collectibles/collectiblesSchema";
import { adjustSportsCardPrice } from "@/services/collectibles/collectiblesPriceHelpers";
import { ParameterElastic_Schema } from "@/services/parameters/parametersSchema";
import { Theme_Schema } from "@/services/themes/themeSchema";
import { CategoryId, CategoryId_Schema } from "@/services/collection/categories/categoriesSchema";
import { ApiResponse_Image_Schema } from "@/services/images";
import { z } from "zod";
import { PriceElastic_Schema } from "@/services/prices/priceSchema";

export type PriceElastic = z.infer<typeof PriceElastic_Schema>;

/** Sports cards have many price entries per collectible; we keep only one — the newest with empty type (raw/ungraded) — and round it up to a clean tier. If no raw price exists, we return nothing rather than falling back to a graded price. */
const normalizeSportsCardPrices = (prices: Record<string, PriceElastic>): PriceElastic[] => {
  // API may return price=0 entries; filter them out as zero is not a valid price (workaround for ColectioOrg/api.colectio.com#403).
  const entries = Object.entries(prices).filter(([, p]) => p.price > 0 && p.type === "");
  if (entries.length === 0) return [];

  const sorted = [...entries].sort(([a], [b]) => {
    const nA = Number(a),
      nB = Number(b);
    if (!isNaN(nA) && !isNaN(nB)) return nB - nA;
    return b.localeCompare(a);
  });

  const [, best] = sorted[0];
  return [{ ...best, price: adjustSportsCardPrice(best.price), type: "" }];
};

export const CollectibleElastic_Schema = z
  .object({
    collectibleId: CollectibleId_Schema,
    categoryId: CategoryId_Schema,
    name: z.string(),
    theme: Theme_Schema.nullable(),
    images: z.array(ApiResponse_Image_Schema),
    // API may return price=0 entries; filter them out as zero is not a valid price (workaround for ColectioOrg/api.colectio.com#403).
    prices: z.record(z.string(), PriceElastic_Schema),
    parameters: ParameterElastic_Schema.array(),
    externalId: z.string().nullable(),
  })
  .transform(({ collectibleId, ...rest }) => ({
    id: collectibleId,
    ...rest,
    prices: rest.categoryId === CategoryId.SportsCards ? normalizeSportsCardPrices(rest.prices) : Object.values(rest.prices).filter((p) => p.price > 0),
  }));
